import { EditOutlined } from '@ant-design/icons';
import { ConnectError } from '@bufbuild/connect';
import { Button, notification, Typography } from 'antd';
import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { Organization } from 'protocol/model/dto_organization_pb';

import { PATHS } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import {
  DISTRIBUTORS_FILTER,
  OrganizationSearchSelect,
  organizationsMessages,
} from '@/entities/organizations';
import { userModel } from '@/entities/user';

import styles from './OrganizationDistributor.module.scss';

const { Text } = Typography;

type OrganizationDistributorProps = {
  canManage: boolean;
  organization: Organization;
  onMarkAsDistributor: () => void;
};

export const OrganizationDistributor: FC<OrganizationDistributorProps> = (
  props,
) => {
  const { canManage, organization, onMarkAsDistributor } = props;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDistributorID, setSelectedDistributorID] = useState<
    string | undefined
  >();

  const distributorID = organization.OnboardedByDistributor?.ID ?? '';
  const distributorEmail = organization.OnboardedByDistributor?.Email ?? '';
  const distributorName = organization.OnboardedByDistributor?.Name ?? '';
  const hasDistributor = Boolean(distributorEmail);
  const isDistributor = Boolean(organization.Distributor);

  // NOTE: DSO and distributor hierarchies are mutually exclusive, so an
  // organization that is a DSO or belongs to one cannot join a distributor.
  const isDSORelated =
    Boolean(organization.IsDSO) || Boolean(organization.DSOParent?.ID);

  const canAssign = canManage && !isDistributor && !isDSORelated;

  const distributorLinkText = distributorName || distributorEmail;
  const distributorSecondaryText = distributorName && distributorEmail;

  const handleCancel = () => {
    setIsEditorVisible(false);
    setSelectedDistributorID(undefined);
  };

  const handleSubmit = async () => {
    if (!selectedDistributorID) {
      return;
    }

    setLoading(true);

    try {
      const { Organization: updatedOrganization } = await dispatch(
        userModel.thunks.setOrganizationDistributor({
          OrganizationID: organization.ID,
          DistributorID: selectedDistributorID,
        }),
      ).unwrap();

      if (updatedOrganization?.ID) {
        dispatch(
          organizationModel.actions.setNewestOrganization(updatedOrganization),
        );
      }

      notification.success({
        message: formatMessage({
          id: 'distributorModal.setSuccess',
          defaultMessage: 'Distributor assigned successfully',
        }),
      });

      setIsEditorVisible(false);
      setSelectedDistributorID(undefined);
    } catch (error) {
      notification.error({
        message:
          error instanceof ConnectError
            ? error.message
            : formatMessage({
                id: 'global.error.somethingWentWrong',
                defaultMessage: 'Something went wrong',
              }),
      });
    } finally {
      setLoading(false);
    }
  };

  // A distributor organization has no distributor of its own, so there is
  // neither a value to show nor an assignment to offer.
  if (isDistributor && !hasDistributor) {
    return null;
  }

  if (isEditorVisible) {
    return (
      <>
        <span className={styles.label}>
          <FormattedMessage
            id="organization.parentDistributor"
            defaultMessage="Parent Distributor"
          />
        </span>
        <div className={styles.value}>
          <div className={styles.formContainer}>
            <OrganizationSearchSelect
              className={styles.select}
              filter={DISTRIBUTORS_FILTER}
              placeholder={formatMessage(
                organizationsMessages.searchPlaceholder,
              )}
              value={selectedDistributorID}
              onChange={setSelectedDistributorID}
            />

            <div className={styles.formButtons}>
              <Button onClick={handleCancel} loading={loading}>
                {formatMessage({
                  id: 'global.cancel',
                  defaultMessage: 'Cancel',
                })}
              </Button>
              <Button
                type="primary"
                disabled={!selectedDistributorID}
                onClick={handleSubmit}
                loading={loading}
              >
                {formatMessage({ id: 'global.save', defaultMessage: 'Save' })}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (hasDistributor) {
    return (
      <>
        <span className={styles.label}>
          <FormattedMessage
            id="organization.parentDistributor"
            defaultMessage="Parent Distributor"
          />
        </span>
        <span className={styles.editableValue}>
          {distributorID ? (
            <Link
              className={styles.distributorLink}
              to={generatePath(PATHS.organization, {
                organizationID: distributorID,
              })}
            >
              {distributorLinkText}
            </Link>
          ) : (
            <span>{distributorLinkText}</span>
          )}

          {!!distributorSecondaryText && (
            <Text className={styles.distributorEmail}>
              ({distributorSecondaryText})
            </Text>
          )}

          {canAssign && (
            <EditOutlined
              className={styles.editIcon}
              onClick={() => setIsEditorVisible(true)}
              rev={undefined}
            />
          )}
        </span>
      </>
    );
  }

  return (
    <>
      <span className={cn(styles.label, styles.centered)}>
        <FormattedMessage
          id="organization.distributor"
          defaultMessage="Distributor"
        />
      </span>
      <div className={styles.actions}>
        <Button
          type="primary"
          disabled={!canManage || isDSORelated}
          onClick={onMarkAsDistributor}
        >
          <FormattedMessage
            id="organization.markAsDistributor"
            defaultMessage="Mark as Distributor"
          />
        </Button>

        <Button
          type="primary"
          disabled={!canAssign}
          onClick={() => setIsEditorVisible(true)}
        >
          <FormattedMessage
            id="organization.assignDistributor"
            defaultMessage="Assign Distributor"
          />
        </Button>
      </div>
    </>
  );
};
